import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { photoPieceMay, showreelVideo, type MediaView, type ProjectView } from "@joogoos/contracts";
import { api, whileStillBeingDescribed } from "../../api.js";
import { Icon } from "../../ui/icons.js";
import { PHOTO_GROUPS, photoGroup, type PhotoGroup } from "../../ui/photos.js";

type Source = PhotoGroup | "found";

const SOURCES: ReadonlyArray<readonly [Source, string, string]> = [...PHOTO_GROUPS, ["found", "Find one", "stock, free to use"]];

/**
 * Zone 3 while the chat is on the photos (03).
 *
 * The rule for this pane is the reason it exists: a choice you have to *look* at does not belong in a chat
 * bubble. Three places a picture can come from, ticked in one grid, and the answer goes back to the chat as one
 * sentence. Uploading happens here too, because "I do not have one yet" is the commonest answer to this question.
 */
export function Photos({ project, onDone }: {
  project: ProjectView;
  /** Called once the answer is taken, whatever it was: the picker opened from the composer closes on it. */
  onDone?: (() => void) | undefined;
}) {
  const qc = useQueryClient();
  // A video of pieces (Showreel, Editorial, Poster, Handmade) shows the photos chosen inside its photo pieces and stays
  // drawn (2026-09-29), so choosing them sets no look, and only the owner's own and those kept from their lookup are
  // offered: never one made or found for them, which a photo piece would not show.
  const reel = showreelVideo(project.draft);
  const sources = reel ? SOURCES.filter(([s]) => s === "yours" || s === "site") : SOURCES;
  const [source, setSource] = useState<Source>("yours");
  const [picked, setPicked] = useState<string[]>(project.draft.sourceMediaIds ?? []);
  const [q, setQ] = useState("");
  const file = useRef<HTMLInputElement>(null);

  const media = useQuery({ queryKey: ["media", "image"], queryFn: () => api.media("image"), refetchInterval: whileStillBeingDescribed });
  const found = useQuery({ queryKey: ["search", q], queryFn: () => api.search(q), enabled: source === "found" && q.trim().length > 1 });
  const upload = useMutation({ mutationFn: api.upload, onSuccess: (m) => { setPicked((p) => [...p, m.id]); void qc.invalidateQueries({ queryKey: ["media", "image"] }); } });
  const answer = useMutation({
    mutationFn: (ids: string[]) => api.answer(project.id, reel ? { sourceMediaIds: ids } : { sourceMediaIds: ids, look: "pictures" },
      ids.length ? `Use ${ids.length} photo${ids.length > 1 ? "s" : ""}` : reel ? "No photos" : "No photos, invent them"),
    onSuccess: (p) => { qc.setQueryData(["project", p.id], p); void qc.invalidateQueries({ queryKey: ["projects"] }); onDone?.(); },
  });
  // No photos at all, and nothing generated either: every shot drawn from the library (ADR 0050). Videos only.
  const draw = useMutation({
    mutationFn: () => api.answer(project.id, { sourceMediaIds: [], look: "drawn" }, "No photos, draw it"),
    onSuccess: (p) => { qc.setQueryData(["project", p.id], p); void qc.invalidateQueries({ queryKey: ["projects"] }); onDone?.(); },
  });

  const library = media.data ?? [];
  // A video of pieces counts and sends only the photos a photo piece may show (review, 2026-09-29): one made for the
  // owner, picked while the video was of pictures from a tab this one hides, was counted and sent unseen, and never
  // shown. One the list does not have yet, an upload landing, is kept.
  const chosen = reel ? picked.filter((id) => { const m = library.find((x) => x.id === id); return !m || photoPieceMay(m); }) : picked;
  const shown: MediaView[] = source === "found" ? (found.data?.results ?? []) : library.filter((m) => photoGroup(m) === source);
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <div className="p-6 flex flex-col gap-3.5 h-full min-h-0">
      <div className="flex items-baseline gap-3">
        <h1 className="text-lg font-semibold">Which photos?</h1>
        <span className="text-[13px] text-muted">{reel ? "tick as many as you like · this style shows them where its story needs them" : "tick as many as you like · they set what the video looks like"}</span>
      </div>

      <div className="flex items-center gap-1.5">
        {sources.map(([s, label, note]) => (
          <button
            key={s}
            onClick={() => setSource(s)}
            title={note}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${source === s ? "bg-ink text-white" : "bg-page text-ink-2 hover:bg-line"}`}
          >
            {label}
          </button>
        ))}
        <button onClick={() => file.current?.click()} className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-line-2 bg-panel text-xs font-medium hover:bg-page">
          <Icon name="plus" size={13} /> Upload
        </button>
        <input ref={file} type="file" accept="image/png,image/jpeg,image/webp" multiple hidden
          onChange={(e) => { for (const f of Array.from(e.target.files ?? [])) upload.mutate(f); e.target.value = ""; }} />
      </div>

      {source === "found" ? (
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="a bakery counter, morning light…"
          className="border border-line-2 rounded-lg px-3 py-2 text-[13px] bg-panel outline-none focus:border-ink/30"
        />
      ) : null}

      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="grid grid-cols-[repeat(auto-fill,minmax(132px,1fr))] gap-2.5">
          {shown.map((m) => (
            <button
              key={m.id}
              onClick={() => toggle(m.id)}
              title={m.caption ?? ""}
              className={`relative rounded-lg overflow-hidden border-2 aspect-[4/5] ${picked.includes(m.id) ? "border-coral" : "border-transparent hover:border-line-2"}`}
            >
              {m.url ? <img src={m.thumbUrl} alt={m.caption ?? ""} loading="lazy" decoding="async" {...(m.width && m.height ? { width: m.width, height: m.height } : {})} className="w-full h-full object-cover" /> : <span className="w-full h-full block bg-line" />}
              {picked.includes(m.id) ? (
                <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-coral text-white grid place-items-center"><Icon name="check" size={12} /></span>
              ) : null}
              {m.caption ? <span className="absolute inset-x-0 bottom-0 px-1.5 py-1 bg-gradient-to-t from-black/70 to-transparent text-white text-[10px] truncate">{m.caption}</span> : null}
            </button>
          ))}
        </div>
        <Empty source={source} q={q} shown={shown.length} available={found.data?.available} note={found.data?.note} searching={found.isFetching} uploading={upload.isPending} />
      </div>

      <div className="flex items-center gap-3 border-t border-line pt-3">
        <span className="text-[12.5px] text-muted">
          {chosen.length ? `${chosen.length} chosen` : "Nothing chosen yet"}
          {chosen.length ? " · choosing them is free" : ""}
        </span>
        {project.draft.kind === "video" && !reel ? (
          <button onClick={() => draw.mutate()} title="headlines, numbers, steps and painted pictures, in your colours" className="ml-auto px-3.5 py-2 rounded-lg border border-line-2 bg-panel text-xs font-medium hover:bg-page">
            None, draw it · free
          </button>
        ) : null}
        <button onClick={() => answer.mutate([])} className={`${project.draft.kind === "video" && !reel ? "" : "ml-auto "}px-3.5 py-2 rounded-lg border border-line-2 bg-panel text-xs font-medium hover:bg-page`}>
          {reel ? "No photos" : "None, invent them"}
        </button>
        <button onClick={() => answer.mutate(chosen)} disabled={!chosen.length || answer.isPending} className="px-4 py-2 rounded-lg bg-ink text-white text-xs font-semibold disabled:opacity-40">
          Use {chosen.length || ""}
        </button>
      </div>
    </div>
  );
}

/** Every way this grid can be empty, said differently — they mean different things to whoever is looking. */
function Empty({ source, q, shown, available, note, searching, uploading }: {
  source: Source; q: string; shown: number; available: boolean | undefined; note: string | undefined; searching: boolean; uploading: boolean;
}) {
  if (uploading) return <P>Uploading…</P>;
  if (shown > 0) return null;
  if (source === "yours") return <P>No photos yet. Upload some — a real photo of your own always beats a generated one.</P>;
  if (source === "site") return <P>Nothing kept from your lookup yet. The Business tab offers pictures when it looks you up.</P>;
  if (source === "made") return <P>Nothing made yet. Pictures from your finished posts land here.</P>;
  if (q.trim().length < 2) return <P>Say what you are looking for.</P>;
  if (searching) return <P>Looking…</P>;
  if (available === false) return <P>{note ?? "Finding pictures for you is not connected yet."} Your own photos and generated ones both work today.</P>;
  return <P>Nothing found for “{q}”.</P>;
}

const P = ({ children }: { children: React.ReactNode }) => <p className="text-[13px] text-muted leading-relaxed max-w-md py-6">{children}</p>;
